<script setup lang="ts">
/**
 * The composition root — the one component a surface mounts.
 *
 * A surface passes a transport and, optionally, a conversation. It never passes
 * a mode, a kind, or a name: every affordance below resolves from
 * `transport.capabilities`. If a component here ever needs to know which surface
 * it is on, the capability set is missing a flag — add the flag, not the branch.
 */
import { computed, ref, watch } from 'vue';

import { conversationsApi, type ConversationKind } from '../api/conversations';
import { useChat } from '../composables/useChat';
import type { ContentPart } from '../types/message';
import type { ChatTransport } from '../types/transport';
import ChatInput from './ChatInput.vue';
import ConversationList from './ConversationList.vue';
import MessageList from './MessageList.vue';

const props = withDefaults(
  defineProps<{
    transport: ChatTransport;
    conversationId?: string;
    /** Enables the history sidebar. Off → a transcript with no persistence UI. */
    history?: boolean;
    /** Discriminates stored conversations. A storage attribute, not a surface
     *  check: nothing branches on it, it is written to and read from the API. */
    kind?: ConversationKind;
    agentId?: string;
    model?: string;
    emptyState?: string;
    allowEphemeral?: boolean;
    ephemeralNotice?: string;
    /** Surfaces a per-turn trace link. Off unless the host handles `@trace`. */
    traceable?: boolean;
    /** Banner shown above the transcript — e.g. an expired-session warning. */
    notice?: string;
  }>(),
  { emptyState: 'Send a message to get started.', kind: 'playground' },
);

const emit = defineEmits<{
  'update:conversationId': [id: string | undefined];
  /** A turn's execution trace was requested. Emitted rather than routed: this
   *  module has no business knowing the host app's router. */
  trace: [taskId: string];
}>();

const activeId = ref(props.conversationId);
watch(
  () => props.conversationId,
  (id) => {
    activeId.value = id;
  },
);

const sidebar = ref<InstanceType<typeof ConversationList> | null>(null);

const chat = useChat({ transport: props.transport, conversationId: activeId });
const capabilities = props.transport.capabilities;

const busy = computed(() => chat.status.value === 'streaming');

/**
 * Reader preference, persisted: a long agent transcript is mostly tool plumbing,
 * and someone reading for the answer should not have to scroll past it every
 * time. Thinking stays — it explains the answer rather than mechanising it.
 */
const HIDE_TOOL_CALLS_KEY = 'asab_chat_hide_tool_calls';
const hideToolCalls = ref(localStorage.getItem(HIDE_TOOL_CALLS_KEY) === '1');
watch(hideToolCalls, (v) => localStorage.setItem(HIDE_TOOL_CALLS_KEY, v ? '1' : '0'));

async function select(id: string) {
  activeId.value = id;
  emit('update:conversationId', id);
  try {
    await chat.loadHistory(id);
  } catch {
    // History is additive: a failed load must not strand the surface. Start a
    // fresh transcript against the same conversation rather than dead-ending.
    chat.reset();
  }
}

function create(ephemeral: boolean) {
  pendingEphemeral.value = ephemeral;
  activeId.value = undefined;
  emit('update:conversationId', undefined);
  chat.reset();
}

const pendingEphemeral = ref(false);

/**
 * Mints the conversation lazily, on the first message rather than on "New chat".
 * Clicking New must not litter the sidebar with empty rows the user never used.
 */
async function ensureConversation(first: ContentPart[]): Promise<void> {
  if (activeId.value) return;
  const firstText = first.find((p) => p.type === 'text');
  const title = firstText?.type === 'text' ? firstText.text.split('\n')[0].slice(0, 80) : '';
  try {
    const conversation = await conversationsApi.create({
      kind: props.kind,
      title,
      agent_id: props.agentId,
      model: props.model,
      ...(pendingEphemeral.value ? { ephemeral: true } : {}),
    });
    activeId.value = conversation.id;
    emit('update:conversationId', conversation.id);
    void sidebar.value?.reload();
  } catch {
    // No conversation means no history for this turn — which is a strictly
    // better outcome than refusing to send the message.
  }
}

async function send(content: ContentPart[]) {
  if (props.history) await ensureConversation(content);
  await chat.send(content, { model: props.model });
}

function approvalFor(messageId: string) {
  return chat.messages.value.find((m) => m.id === messageId)?.approval;
}

async function approve(messageId: string, approved: boolean) {
  const approval = approvalFor(messageId);
  if (approval) await chat.approve(approval.toolCallId, approved);
}

async function submitResult(messageId: string, output: string, isError: boolean) {
  const approval = approvalFor(messageId);
  if (approval) await chat.submitToolResult(approval.toolCallId, isError ? `ERROR: ${output}` : output);
}
</script>

<template>
  <div data-testid="chat-window" class="flex h-full">
    <ConversationList
      v-if="history"
      ref="sidebar"
      :kind="kind"
      :agent-id="agentId"
      :active-id="activeId"
      :allow-ephemeral="allowEphemeral"
      :ephemeral-notice="ephemeralNotice"
      @select="select"
      @create="create"
      @deleted="(id) => id === activeId && create(false)"
    />

    <div class="flex min-w-0 flex-1 flex-col">
      <p
        v-if="notice"
        data-testid="chat-notice"
        class="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300"
      >
        {{ notice }}
      </p>
      <div
        v-if="capabilities.steps"
        class="flex shrink-0 items-center justify-end border-b border-gray-100 px-4 py-1.5 dark:border-gray-800"
      >
        <label class="flex cursor-pointer items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
          <input v-model="hideToolCalls" type="checkbox" data-testid="hide-tool-calls" class="rounded" />
          Hide tool calls
        </label>
      </div>

      <MessageList
        :messages="chat.messages.value"
        :capabilities="capabilities"
        :busy="busy"
        :traceable="traceable"
        :hide-tool-calls="hideToolCalls"
        @approve="(id) => approve(id, true)"
        @deny="(id) => approve(id, false)"
        @submit-result="submitResult"
        @trace="(taskId) => emit('trace', taskId)"
      >
        <template #empty>
          <div
            v-if="!chat.messages.value.length"
            data-testid="chat-empty"
            class="py-12 text-center text-sm text-gray-400"
          >
            <p>{{ emptyState }}</p>
            <slot name="suggestions" />
          </div>
        </template>
      </MessageList>

      <p
        v-if="chat.error.value"
        data-testid="chat-error"
        class="px-4 py-1 text-xs text-red-600 dark:text-red-400"
      >
        {{ chat.error.value }}
      </p>

      <ChatInput :capabilities="capabilities" :busy="busy" @submit="send" @cancel="chat.cancel">
        <template #picker>
          <slot name="picker" />
        </template>
      </ChatInput>
    </div>
  </div>
</template>
